/**
 * SoloTablesOverlay — in-solo panel listing live cee-lo / craps tables. Opened
 * from the solo "Tables" button. Reuses the lobby's RoomList / RoomCell so the
 * rows match the lobby exactly (game logos, avatar stacks, FULL handling).
 * Tapping JOIN leaves solo and joins the table directly.
 *
 * Single always-mounted overlay on document.body; `.visible` is the only state.
 * The fade-in commits its hidden base first (reflow) per the Android flicker rule.
 */

import { RoomList } from './lobby/RoomList';
import type { LobbyRoom, LobbyPlayer } from '../../services/LobbyService';

interface SoloTablesCallbacks {
  onJoin: (roomId: string) => void;
  onClose: () => void;
}

class SoloTablesOverlayImpl {
  private root: HTMLElement | null = null;
  private emptyEl: HTMLElement | null = null;
  private roomList: RoomList | null = null;
  private cb: SoloTablesCallbacks | null = null;

  private ensure(getPlayer: (id: string) => LobbyPlayer | undefined): void {
    if (this.root) return;
    this.injectStyles();

    const root = document.createElement('div');
    root.className = 'solo-tables-overlay';
    root.innerHTML = `
      <div class="solo-tables-panel">
        <div class="solo-tables-head">
          <span class="solo-tables-title">Live Tables</span>
          <button class="solo-tables-close" aria-label="Close">&times;</button>
        </div>
        <div class="solo-tables-body">
          <div class="solo-tables-empty" hidden>No tables open right now. Keep grinding... one will pop.</div>
        </div>
      </div>`;
    document.body.appendChild(root);

    root.addEventListener('click', (e) => { if (e.target === root) this.cb?.onClose(); });
    root.querySelector('.solo-tables-close')?.addEventListener('click', () => this.cb?.onClose());

    const body = root.querySelector('.solo-tables-body') as HTMLElement;
    this.emptyEl = root.querySelector('.solo-tables-empty');
    this.roomList = new RoomList(getPlayer);
    this.roomList.on('join:tap', (e) => this.cb?.onJoin(e.roomId));
    body.appendChild(this.roomList.element);

    this.root = root;
  }

  show(opts: {
    rooms: LobbyRoom[];
    getPlayer: (id: string) => LobbyPlayer | undefined;
    onJoin: (roomId: string) => void;
    onClose: () => void;
  }): void {
    this.cb = { onJoin: opts.onJoin, onClose: opts.onClose };
    this.ensure(opts.getPlayer);
    this.applyRooms(opts.rooms);
    const root = this.root!;
    void root.offsetHeight; // commit hidden base before fade-in
    root.classList.add('visible');
  }

  hide(): void {
    this.root?.classList.remove('visible');
  }

  isOpen(): boolean {
    return !!this.root?.classList.contains('visible');
  }

  /** Live-refresh while the panel is open. */
  setRooms(rooms: LobbyRoom[]): void {
    if (this.isOpen()) this.applyRooms(rooms);
  }

  private applyRooms(rooms: LobbyRoom[]): void {
    if (!this.roomList || !this.emptyEl) return;
    this.roomList.setRooms(rooms);
    const empty = rooms.length === 0;
    this.emptyEl.hidden = !empty;
    this.roomList.element.style.display = empty ? 'none' : '';
  }

  private injectStyles(): void {
    if (document.getElementById('solo-tables-styles')) return;
    const style = document.createElement('style');
    style.id = 'solo-tables-styles';
    style.textContent = `
      .solo-tables-overlay {
        position: fixed; inset: 0; z-index: 810;
        display: flex; align-items: center; justify-content: center;
        opacity: 0; pointer-events: none; transition: opacity 220ms ease;
        background: radial-gradient(ellipse at center, rgba(8,8,10,0.55), rgba(8,8,10,0.82));
      }
      .solo-tables-overlay.visible { opacity: 1; pointer-events: auto; }
      .solo-tables-panel {
        width: min(94vw, 420px); max-height: 76vh; display: flex; flex-direction: column;
        background: linear-gradient(160deg, rgba(24,24,30,0.98), rgba(14,14,18,0.98));
        border: 2px solid rgba(255,255,255,0.12); border-radius: 18px; overflow: hidden;
        transform: translateY(12px) scale(0.98); transition: transform 220ms cubic-bezier(0.2,0.9,0.3,1);
      }
      .solo-tables-overlay.visible .solo-tables-panel { transform: translateY(0) scale(1); }
      .solo-tables-head {
        display: flex; align-items: center; justify-content: space-between;
        padding: 14px 16px; border-bottom: 1px solid rgba(255,255,255,0.08);
      }
      .solo-tables-title {
        font-family: 'Bebas Neue', Impact, sans-serif; font-size: 24px; letter-spacing: 1.5px; color: #F2F2F5;
      }
      .solo-tables-close {
        background: none; border: none; color: #9A9AA4; font-size: 26px; line-height: 1;
        cursor: pointer; padding: 0 4px;
      }
      .solo-tables-body { overflow-y: auto; padding-top: 12px; }
      .solo-tables-empty { padding: 30px 18px; text-align: center; color: #9A9AA4; font-size: 14px; line-height: 1.5; }
    `;
    document.head.appendChild(style);
  }
}

/** Always-mounted singleton (created on first show). */
export const soloTablesOverlay = new SoloTablesOverlayImpl();
